import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { FormInput, FormLabel, FormRow } from '@ui/components/Form/Form';
import { Tabs } from '@ui/components/Tabs';

import { getClubMeta } from '@features/club/utils/club-meta';
import {
  buildSectionTickets,
  getSectionSubTickets,
  isTicketedSection,
} from '@features/club/utils/event-sections';
import { TournamentEditFormValues } from '@features/tournament/ui/components/TournamentSettings/typings';

const ALL_SUB_OPTION = { value: '', label: 'Все' };

export function EventSectionInput({ clubId }: { clubId: string }) {
  const { control, setValue } = useFormContext<TournamentEditFormValues>();
  const entranceTicketSets = useWatch({ name: 'entranceTicketSets', control });

  const sections = useMemo(
    () => getClubMeta(clubId).eventSections.filter(isTicketedSection),
    [clubId],
  );
  const tickets = useMemo(() => entranceTicketSets?.flat() ?? [], [entranceTicketSets]);

  const selected = sections.find((s) => tickets.includes(s.ticket));
  const subOptions = useMemo(() => (selected ? getSectionSubTickets(selected) : []), [selected]);
  const selectedSub = subOptions.find((o) => tickets.includes(o.value))?.value ?? '';

  // Новый или старый турнир без раздела попадает в первый раздел
  const first = sections[0];
  useEffect(() => {
    if (!selected && first) {
      setValue('entranceTicketSets', buildSectionTickets(first), { shouldDirty: true });
    }
  }, [selected, first, setValue]);

  if (!first) return null;

  return (
    <>
      <FormRow>
        <FormLabel>Раздел</FormLabel>
        <FormInput>
          <Controller
            name="entranceTicketSets"
            control={control}
            render={({ field: { onChange, disabled } }) => (
              <Tabs
                tabs={sections.map((s) => ({ value: s.id, label: s.title ?? 'События' }))}
                value={selected?.id}
                onChange={(id) =>
                  onChange(buildSectionTickets(sections.find((s) => s.id === id) ?? first))
                }
                disabled={disabled}
              />
            )}
          />
        </FormInput>
      </FormRow>
      {selected && subOptions.length > 0 && (
        <FormRow>
          <FormLabel>Подраздел</FormLabel>
          <FormInput>
            <Controller
              name="entranceTicketSets"
              control={control}
              render={({ field: { onChange, disabled } }) => (
                <Tabs
                  tabs={[ALL_SUB_OPTION, ...subOptions]}
                  value={selectedSub}
                  onChange={(t) => onChange(buildSectionTickets(selected, t || undefined))}
                  disabled={disabled}
                />
              )}
            />
          </FormInput>
        </FormRow>
      )}
    </>
  );
}
